import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { usersApi } from '@/services/users';
import { AUTH_USER_CHANGED_EVENT, tokenStorage } from '@/utils/token-storage';

// Sprint 44 bug fix — permissions granted mid-session (e.g. the new
// perm-lead-redact-ip) only took effect after a full re-login: login snapshots
// the user (with permissions/roles) into localStorage and nothing refreshed it
// until the access token happened to expire. This hook keeps the stored user
// in step with the server while the app shell is mounted: it refetches the
// current user on mount / window focus / a slow interval, rewrites the stored
// copy, and returns a version counter that bumps only when the permission or
// role set actually changed — so the shell re-renders and permission-gated UI
// (nav rail, mobile drawer, and anything else rendered beneath it) re-reads
// authUtils against fresh data. Server-side enforcement is already live-per-
// request (JwtStrategy re-fetches permissions from the DB); this closes the
// client-side gap.

const REFRESH_INTERVAL_MS = 5 * 60 * 1000;
const STALE_TIME_MS = 60 * 1000;

/**
 * Stable signature of a user's access set. Handles both stored shapes:
 * login payload (`permissions: [{ permission: { id, name } }]`, same for
 * roles) and the legacy plain string arrays.
 */
export function accessSignature(user: any): string {
  if (!user) return '';
  const perms: string[] = Array.isArray(user.permissions)
    ? user.permissions
        .map((p: any) =>
          typeof p === 'object' && p?.permission
            ? p.permission.name || p.permission.id
            : p,
        )
        .filter(Boolean)
    : [];
  const roles: string[] = Array.isArray(user.roles)
    ? user.roles
        .map((r: any) =>
          typeof r === 'object' && r?.role ? r.role.id || r.role.name : r,
        )
        .filter(Boolean)
    : [];
  return JSON.stringify({
    isOwner: user.isOwner === true,
    perms: [...perms].sort(),
    roles: [...roles].sort(),
  });
}

/**
 * Keeps localStorage user data (permissions/roles) synced with the server.
 * Returns a number that increments whenever the access set changes, so the
 * caller (AppShell) re-renders its subtree.
 */
export function usePermissionsSync(): number {
  const [version, setVersion] = React.useState(0);
  const [userId, setUserId] = React.useState<string | undefined>(
    () => tokenStorage.getUserData()?.id,
  );

  React.useEffect(() => {
    const sync = () => {
      const nextId = tokenStorage.getUserData()?.id;
      setUserId((prev) => {
        if (prev !== nextId) setVersion((v) => v + 1);
        return nextId;
      });
    };
    window.addEventListener(AUTH_USER_CHANGED_EVENT, sync);
    return () => window.removeEventListener(AUTH_USER_CHANGED_EVENT, sync);
  }, []);

  const { data: freshUser } = useQuery({
    queryKey: ['permissions-sync', userId],
    queryFn: () => usersApi.getUserById(userId!),
    enabled: Boolean(userId && tokenStorage.hasToken()),
    staleTime: STALE_TIME_MS,
    refetchInterval: REFRESH_INTERVAL_MS,
    refetchOnWindowFocus: true,
    retry: 1,
  });

  React.useEffect(() => {
    if (!freshUser) return;
    const prev = tokenStorage.getUserData();
    // Guard against a race with logout/re-login: only apply when the
    // response still describes the currently stored user.
    if (!prev || prev.id !== (freshUser as any).id) return;
    const nextUser = {
      ...freshUser,
      // GET /users/:id must not drop a known Owner flag. Server `false`
      // still wins (ownership transfer); omitted / undefined keeps prev.
      isOwner:
        (freshUser as any).isOwner === true ||
        ((freshUser as any).isOwner !== false && prev.isOwner === true),
    };
    const changed = accessSignature(prev) !== accessSignature(nextUser);
    tokenStorage.setUserData(nextUser);
    if (changed) setVersion((v) => v + 1);
  }, [freshUser]);

  return version;
}
